import React, { useContext, useState, useRef, useEffect } from "react";
import style from "./AppDashboard.module.css";

// Components
import PromptInputContainer from "../../components/PromptInputContainer/PromptInputContainer";
import BackgroundDots from "../../components/BackgroundDots.tsx";
import RateLimitToast from '../../components/RateLimitToast/RateLimitToast';

// Context
import { AppContext } from "../../context/appContext";

// Functions
import { generateImage } from "../../api";

// Utils
import { COMMON_WORDS } from "../../utils/commonWords";

// Hooks
import { useDebounce } from "../../hooks/useDebounce";

// Images
import { AppVideoContent } from "./AppVideoContent";


export const HomePage = () => {
  const {
    isVideo,
    setAlertOptions,
    isLoading,
    setIsLoading,
    setIsVideo,
    setVideoOptions,
    videoOptions,
    userId,
  } = useContext(AppContext);

  const [dropDownOptions, setDropDownOptions] = useState({
    style: "Digital",
    mode: "Tovera",
    length: "Auto",
    voice: "Male",
  });

  const [ideaInputText, setIdeaInputText] = useState("");
  const [generatedImageUrl, setGeneratedImageUrl] = useState("");
  const [showNotification, setShowNotification] = useState(false);
  const [lastProcessedText, setLastProcessedText] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState("");

  const notificationTimeoutRef = useRef(null);
  const inputRef = useRef(null);

  const [showRateLimitToast, setShowRateLimitToast] = useState(false);
  const [rateLimitDuration, setRateLimitDuration] = useState(0);

  useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);

  const processText = async (text) => {
    const words = text.trim().split(/[\s.,!?]+/).filter(Boolean);
    const newWords = words.filter((word) => !COMMON_WORDS.has(word.toLowerCase()));
  
    if (newWords.length > 0 && text !== lastProcessedText) {
      try {
        const imageUrl = await generateImage(text, userId); // Ensure userId is defined
        setGeneratedImageUrl(imageUrl);
        setLastProcessedText(text);
      } catch (error) {
        console.error("Error generating image:", error);
      }
    }
  };
  

  const debouncedProcessText = useDebounce(processText, 300, 3);

  const handleChange = (event) => {
    const text = event.target.value;
    if (text.length <= 700) {
      setIdeaInputText(text);
    }
    if (text.length === 700) {
      setShowNotification(true);
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
      notificationTimeoutRef.current = setTimeout(() => {
        setShowNotification(false);
      }, 3000);
    }

    if (text.length === 0) {
      setGeneratedImageUrl("");
      setLastProcessedText("");
      return;
    }

    debouncedProcessText(text);
  };

  const handlePresetPromptClick = async (promptText) => {
    setIdeaInputText(promptText);

    try {
      const imageUrl = await generateImage(promptText);
      setGeneratedImageUrl(imageUrl);
    } catch (error) {
      console.error("Error generating image:", error);
    } finally {
    }
  };

  return (
    <div className={style["home-page"]}>
      <BackgroundDots
        dotSize={1.2}
        dotColor="#fb3a5d"
        backgroundColor="transparent"
        gap={15}
        fade={true}
        className={style["background-dots"]}
      />
      {!isVideo ? (
        <div className={style["initial-ui-container"]}>
          <div className={style["preview-input-container"]}>
            <PromptInputContainer
              setIdeaInputText={setIdeaInputText}
              interimTranscript={interimTranscript}
              setInterimTranscript={setInterimTranscript}
              isListening={isListening}
              setIsListening={setIsListening}
              onPresetPromptClick={handlePresetPromptClick}
              generatedImageUrl={generatedImageUrl}
              isLoading={isLoading}
              handleChange={handleChange}
              ideaInputText={ideaInputText}
              showNotification={showNotification}
              inputRef={inputRef}
              dropDownOptions={dropDownOptions}
              setDropDownOptions={setDropDownOptions}
              setAlertOptions={setAlertOptions}
              setIsLoading={setIsLoading}
              setIsVideo={setIsVideo}
              setVideoOptions={setVideoOptions}
              videoOptions={videoOptions}
            />
          </div>
          <div className={style["disclaimer"]}></div>
        </div>
      ) : (
        <AppVideoContent />
      )}
      {showRateLimitToast && (
        <RateLimitToast
          message="Rate limit exceeded. Please wait before generating again."
          duration={rateLimitDuration}
          onClose={() => setShowRateLimitToast(false)}
        />
      )}
    </div>
  );
};
